// src/pages/task/task-management-page/TaskManagementPage.tsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import {
  SaveOutlined,
  RollbackOutlined,
  ReloadOutlined,
  SendOutlined,
  FieldTimeOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment-timezone";

const NZ_TIMEZONE = "Pacific/Auckland";
const toNZTime = (date: any) => date ? moment.utc(date).tz(NZ_TIMEZONE) : null;
import { ApiRequest } from "../../../services/api/api";
import { getUserId } from "../../../services/lib/utils/auth.utils";
import { calculateStatus, Status } from "../task-status-utils";
import { taskCommentTypeForApi } from "../task-comment-utils";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const STATUS_OPTIONS: Status[] = [
  "Not Started",
  "In Progress",
  "Completed",
  "Overdue",
];

const STATUS_COLOR_MAP: Record<Status, string> = {
  "Not Started": "#8c8c8c",
  "In Progress": "#52c41a",
  "Completed": "#000000",
  Overdue: "#f5222d",
};

const normalizeStatus = (value: any): Status => {
  const text = String(value ?? "").trim().toLowerCase();
  switch (text) {
    case "in_progress":
    case "in progress":
    case "in-progress":
      return "In Progress";
    case "completed":
    case "complete":
    case "done":
      return "Completed";
    case "overdue":
    case "blocked":
    case "late":
      return "Overdue";
    case "not started":
    case "not_started":
    case "not-started":
    default:
      return "Not Started";
  }
};

type Task = {
  id: number;
  title: string;
  description?: string;
  managerId?: number | null;
  managerName?: string;
  assignees?: { userId: number; name: string; exec?: string }[];
  followerIds?: number[];
  startDate?: string | null;
  dueDate?: string | null;
  checkpoints?: {
    checkpointId?: number;
    name: string;
    dueDate?: string | null;
    isCompleted?: boolean;
    completedAt?: string | null;
    managerId?: number | null;
  }[];
  /** 接口返回的原始状态（用于 calculateStatus 的 Completed 锁定等） */
  apiStatus: Status;
  /** 与表单一致的推导展示状态（用于 diff、表单初始值） */
  status: Status;
  participants?: Participant[];
};

type CommentType = "Routine" | "Apply Reschedule" | "Apply Completion" | "Reschedule";

type Participant = {
  userId: number;
  name: string;
  role: string;
  executionDetails?: string;
};

type EmployeeOption = { value: number; label: string };

/* ---------------- Mock & API placeholders ---------------- */
const USE_MOCK = false;

let employeeCache: EmployeeOption[] | null = null;

async function fetchEmployees(): Promise<EmployeeOption[]> {
  if (USE_MOCK) {
    const mock = ["Alice", "Bob", "Chris", "Diana", "Evan", "Mandy", "Jason"].map((name, idx) => ({
      value: idx + 1,
      label: name,
    }));
    return mock;
  }
  if (employeeCache) {
    return employeeCache;
  }
  const result = await ApiRequest({
    otherUrl: "Employee/GetAllEmployee",
    type: "get",
  });
  const employees = result?.data?.data ?? result?.data ?? [];
  const options: EmployeeOption[] = (employees || []).map((emp: any) => ({
    value: emp.employeeId ?? emp.EmployeeId,
    label: `${emp.firstName ?? emp.FirstName ?? ""} ${emp.lastName ?? emp.LastName ?? ""}`.trim() ||
      emp.userName ||
      emp.UserName ||
      `Employee ${emp.employeeId ?? emp.EmployeeId}`,
  }));
  employeeCache = options;
  return options;
}

async function apiGetTask(id: string): Promise<Task> {
  if (USE_MOCK) {
    const mockApiStatus: Status = "In Progress";
    const startIso = moment().add(-1, "day").toISOString();
    const dueIso = moment().add(6, "day").toISOString();
    const mockCheckpoints = [
      {
        checkpointId: 0,
        name: "Checkpoint A",
        dueDate: moment().add(2, "day").toISOString(),
        isCompleted: false,
        completedAt: null as string | null,
      },
      {
        checkpointId: 0,
        name: "Checkpoint B",
        dueDate: moment().add(5, "day").toISOString(),
        isCompleted: false,
        completedAt: null as string | null,
      },
    ];
    const derivedStatus = calculateStatus(
      mockApiStatus,
      toNZTime(startIso),
      toNZTime(dueIso),
      mockCheckpoints
    );
    return {
      id: parseInt(id, 10),
      title: "Task Title A",
      description:
        "Editable task form. Changes will auto-generate a reschedule comment.",
      managerId: 1,
      managerName: "Alice",
      assignees: [
        { userId: 2, name: "Bob", exec: "Pack 200 cartons" },
        { userId: 3, name: "Chris", exec: "Arrange pickup with courier" },
      ],
      followerIds: [4],
      startDate: startIso,
      dueDate: dueIso,
      checkpoints: mockCheckpoints,
      apiStatus: mockApiStatus,
      status: derivedStatus,
      participants: [
        { userId: 1, name: "Alice", role: "manager" },
        { userId: 2, name: "Bob", role: "assignee" },
        { userId: 3, name: "Chris", role: "assignee" },
        { userId: 4, name: "Diana", role: "follower" },
      ],
    };
  }
  const taskId = parseInt(id, 10);
  if (!taskId) {
    throw new Error("Invalid task id.");
  }
  const result = await ApiRequest({
    otherUrl: "TaskManagement/GetTaskById",
    type: "get",
    params: { taskId },
  });
  const data = result?.data?.data ?? result?.data;
  if (!data) {
    throw new Error("Task not found.");
  }
  
  // 先构建 participants 数组
  const participants: Participant[] = (data.participants ?? data.Participants ?? []).map(
    (p: any) => {
      const userId = parseInt(String(p?.userId ?? p?.UserId ?? 0), 10);
      return {
        userId: Number.isFinite(userId) ? userId : 0,
        name: p?.name ?? p?.Name ?? "",
        role: (p?.role ?? p?.Role ?? "").toLowerCase(),
        executionDetails:
          p?.executionDetails ??
          p?.ExecutionDetails ??
          p?.executeDetails ??
          p?.ExecuteDetails ??
          p?.details ??
          p?.Details ??
          "",
      };
    }
  );
  
  // 从 participants 中提取 assignees
  const assignees = participants
    .filter((p) => p.role === "assignee" && p.userId > 0)
    .map((p) => ({
      userId: p.userId,
      name: p.name,
      exec: p.executionDetails || "",
    }));
  
  // 从 participants 中提取 followerIds
  const followerIds = participants
    .filter((p) => p.role === "follower" && p.userId > 0)
    .map((p) => p.userId);
  
  const checkpoints = (data.checkpoints ?? data.Checkpoints ?? []).map((c: any) => ({
    checkpointId: c?.checkpointId ?? c?.CheckpointId ?? 0,
    name: c?.name ?? c?.Name ?? "",
    dueDate: c?.dueDate ?? c?.DueDate ?? null,
    isCompleted: !!(c?.isCompleted ?? c?.IsCompleted),
    completedAt: c?.completedAt ?? c?.CompletedAt ?? null,
    managerId: c?.managerId ?? c?.ManagerId ?? null,
  }));

  const apiStatus = normalizeStatus(data.status ?? data.Status ?? "Not Started");
  const startRaw = data.startDate ?? data.StartDate ?? null;
  const dueRaw = data.dueDate ?? data.DueDate ?? null;
  const startM = startRaw ? toNZTime(startRaw) : null;
  const dueM = dueRaw ? toNZTime(dueRaw) : null;
  const status = calculateStatus(apiStatus, startM, dueM, checkpoints);

  return {
    id: data.taskId ?? data.TaskId ?? taskId,
    title: data.title ?? data.Title ?? "",
    description: data.description ?? data.Description ?? "",
    managerId: data.managerId ?? data.ManagerId ?? null,
    managerName: data.managerName ?? data.ManagerName ?? data.manager ?? data.Manager ?? "",
    assignees,
    followerIds,
    startDate: startRaw,
    dueDate: dueRaw,
    checkpoints,
    apiStatus,
    status,
    participants,
  };
}

async function apiUpdateTask(id: number, payload: any) {
  if (USE_MOCK) {
    console.log("[MOCK] update task", id, payload);
    await new Promise((r) => setTimeout(r, 400));
    return;
  }
  const response = await ApiRequest({
    otherUrl: "TaskManagement/UpdateTask",
    method: "put",
    params: { taskId: id },
    data: payload,
    isShowSpinner: true,
  });

  const result = response?.data ?? response;
  const success =
    typeof result?.success === "boolean"
      ? result.success
      : typeof result?.Success === "boolean"
      ? result.Success
      : true;

  if (!success) {
    const errorMessage =
      result?.errorMessage?.message ??
      result?.errorMessage ??
      "Task update failed";
    throw new Error(errorMessage);
  }

  const finalResult = result?.data ?? result?.Data ?? true;
  if (process.env.NODE_ENV !== "production") {
    console.log("[TaskManagement] UpdateTask response", finalResult, result);
  }
  return finalResult;
}

async function apiSearchUsers(keyword: string) {
  const options = await fetchEmployees();
  const k = (keyword || "").toLowerCase();
  return options.filter((opt) =>
    opt.label.toLowerCase().includes(k)
  );
}

function resolveCurrentUserId(): number {
  const raw = getUserId();
  const id = raw != null ? parseInt(String(raw), 10) : 0;
  return Number.isFinite(id) && id > 0 ? id : 0;
}

async function apiAddComment(taskId: number, payload: { type: CommentType; content: string; notifyUserIds: number[] }) {
  if (USE_MOCK) {
    console.log("[MOCK] add comment", taskId, payload);
    return;
  }
  const createdBy = resolveCurrentUserId();
  if (!createdBy) {
    throw new Error("Current user is not identified.");
  }
  await ApiRequest({
    otherUrl: "TaskComment/AddComment",
    method: "post",
    data: {
      taskId,
      createdBy,
      commentType: taskCommentTypeForApi(payload.type),
      content: payload.content,
      notifyUserIds: payload.notifyUserIds,
    },
    isShowSpinner: true,
  });
}

async function apiCreateNotice(taskId: number, text: string, notifyUserIds: number[]) {
  if (USE_MOCK) {
    console.log("[MOCK] notice", { taskId, text, notifyUserIds });
    return;
  }
  const createdBy = resolveCurrentUserId();
  if (!createdBy) {
    throw new Error("Current user is not identified.");
  }
  await ApiRequest({
    otherUrl: "TaskComment/AddComment",
    method: "post",
    data: {
      taskId,
      createdBy,
      commentType: "Notice",
      content: text,
      notifyUserIds,
    },
    isShowSpinner: true,
  });
}
/* -------------------------------------------------------- */

const fmt = (d?: string | null) =>
  d ? toNZTime(d)?.format("YYYY/MM/DD") : "-";

/** 生成“Reschedule”用的变更摘要 */
function buildDiffSummary(
  oldV: Task,
  curV: Task,
  resolveName: (id: number | null | undefined) => string
): string[] {
  const lines: string[] = [];

  const cmp = (label: string, a?: any, b?: any) => {
    if (JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)) {
      lines.push(`${label}: '${a ?? "-"}' → '${b ?? "-"}'`);
    }
  };

  cmp("Title", oldV.title, curV.title);
  cmp("Description", oldV.description, curV.description);
  cmp(
    "Manager",
    oldV.managerName ?? resolveName(oldV.managerId ?? null),
    curV.managerName ?? resolveName(curV.managerId ?? null)
  );
  cmp("Start Date", oldV.startDate ? fmt(oldV.startDate) : "-", curV.startDate ? fmt(curV.startDate) : "-");
  cmp("Due Date", oldV.dueDate ? fmt(oldV.dueDate) : "-", curV.dueDate ? fmt(curV.dueDate) : "-");
  cmp("Status", oldV.status, curV.status);

  const oldAss = (oldV.assignees || [])
    .map((x) => `${x.name || resolveName(x.userId)}${x.exec ? `(${x.exec})` : ""}`)
    .join(", ");
  const newAss = (curV.assignees || [])
    .map((x) => `${x.name || resolveName(x.userId)}${x.exec ? `(${x.exec})` : ""}`)
    .join(", ");
  cmp("Assignees", oldAss, newAss);

  const oldFol = (oldV.followerIds || [])
    .map((id) => resolveName(id))
    .join(", ");
  const newFol = (curV.followerIds || [])
    .map((id) => resolveName(id))
    .join(", ");
  cmp("Followers", oldFol, newFol);

  const oldCk = (oldV.checkpoints || [])
    .map((c) => `${c.name}${c.dueDate ? `@${fmt(c.dueDate)}` : ""}`)
    .join(", ");
  const newCk = (curV.checkpoints || [])
    .map((c) => `${c.name}${c.dueDate ? `@${fmt(c.dueDate)}` : ""}`)
    .join(", ");
  cmp("Checkpoints", oldCk, newCk);

  return lines;
}

const TaskManagementPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userOpts, setUserOpts] = useState<EmployeeOption[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [origin, setOrigin] = useState<Task | null>(null);

  // 评论&通知弹窗
  const [commentVisible, setCommentVisible] = useState(false);
  const [commentType, setCommentType] =
    useState<CommentType>("Routine");
  const [commentContent, setCommentContent] = useState("");
  const [noticeVisible, setNoticeVisible] = useState(false);
  const [noticeVisibility, setNoticeVisibility] =
    useState<"All" | "Selected">("Selected");
  const [noticePeople, setNoticePeople] = useState<string[]>([]);
  const [peopleOptions, setPeopleOptions] = useState<{ value: string; label: string }[]>([]);
  const [computedStatus, setComputedStatus] = useState<Status>("Not Started");

  const buildParticipantOptions = () =>
    (participants || [])
      .filter((p) => Number.isFinite(p.userId) && p.userId > 0)
      .map((p) => ({
        value: String(p.userId),
        label: p.name || `User ${p.userId}`,
      }));

  const load = async () => {
    try {
      setLoading(true);
      const employees = await fetchEmployees();
      setUserOpts(employees);
      const taskData = await apiGetTask(id);
      if (process.env.NODE_ENV !== "production") {
        console.log("[TaskManagement] Loaded task status", taskData.status, taskData);
      }
      setOrigin(taskData);
      const participantList = taskData.participants || [];
      setParticipants(participantList);
      setPeopleOptions(
        participantList
          .filter((p) => Number.isFinite(p.userId) && p.userId > 0)
          .map((p) => ({
            value: String(p.userId),
            label: p.name || `User ${p.userId}`,
          }))
      );
      form.setFieldsValue({
        title: taskData.title,
        description: taskData.description,
        managerId: taskData.managerId ?? undefined,
        assignees: (taskData.assignees || []).map((x) => ({
          userId: x.userId,
          exec: x.exec,
        })),
        followerIds: taskData.followerIds || [],
        startDate: taskData.startDate ? toNZTime(taskData.startDate) : undefined,
        dueDate: taskData.dueDate ? toNZTime(taskData.dueDate) : undefined,
        checkpoints: (taskData.checkpoints || []).map((c: any) => ({
          checkpointId: c.checkpointId || 0,
          name: c.name,
          dueDate: c.dueDate ? toNZTime(c.dueDate) : undefined,
          isCompleted: c.isCompleted ?? false,
          completedAt: c.completedAt,
          managerId: c.managerId ?? undefined,
        })),
        status: taskData.status,
      });
      document.title = "Task Management";
    } catch (e: any) {
      message.error(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 监听 startDate 和 dueDate 变化，自动更新状态
  const startDate = Form.useWatch("startDate", form);
  const dueDate = Form.useWatch("dueDate", form);
  const checkpointsWatch = Form.useWatch("checkpoints", form);
  
  useEffect(() => {
    if (origin) {
      // 将表单中的 checkpoints 转换为计算状态所需格式
      const checkpointsForCalc = (checkpointsWatch || []).map((c: any) => ({
        checkpointId: c?.checkpointId || 0,
        name: c?.name,
        dueDate: c?.dueDate ? (c.dueDate as moment.Moment).toISOString() : null,
        isCompleted: c?.isCompleted ?? false,
        completedAt: c?.completedAt,
      }));
      
      const calculatedStatus = calculateStatus(
        origin.apiStatus,
        startDate,
        dueDate,
        checkpointsForCalc
      );
      setComputedStatus(calculatedStatus);
      form.setFieldValue("status", calculatedStatus);
    }
  }, [startDate, dueDate, checkpointsWatch, origin, form]);

  useEffect(() => {
    if (noticeVisibility === "All") {
      setNoticePeople([]);
    }
  }, [noticeVisibility]);

  const onSearchUsers = async (kw: string) => {
    const opts = await apiSearchUsers(kw);
    setUserOpts(opts);
  };

  const onUserFocus = async () => {
    const opts = await fetchEmployees();
    setUserOpts(opts);
  };

  const currentValue = (): Task => {
    const v = form.getFieldsValue();
    const managerId = v.managerId ? parseInt(String(v.managerId), 10) : null;
    const managerName =
      managerId != null
        ? userOpts.find((opt) => opt.value === managerId)?.label || origin?.managerName || ""
        : "";
    const assigneeEntries = (v.assignees || []).map((x: any) => {
      const userId = x?.userId ? parseInt(String(x.userId), 10) : 0;
      const label = userOpts.find((opt) => opt.value === userId)?.label || "";
      return {
        userId,
        name: label,
        exec: x?.exec || "",
      };
    });
    const followerIds = (v.followerIds || [])
      .map((fid: any) => parseInt(String(fid), 10))
      .filter((fid: number) => Number.isFinite(fid) && fid > 0);
    return {
      id: origin?.id ?? parseInt(id, 10),
      title: v.title || "",
      description: v.description || "",
      managerId,
      managerName,
      assignees: assigneeEntries,
      followerIds,
      startDate: v.startDate ? (v.startDate as moment.Moment).toISOString() : null,
      dueDate: v.dueDate ? (v.dueDate as moment.Moment).toISOString() : null,
      checkpoints: (v.checkpoints || []).map((c: any) => ({
        checkpointId: c?.checkpointId || 0,
        name: c?.name || "",
        dueDate: c?.dueDate ? (c.dueDate as moment.Moment).toISOString() : null,
        managerId: c?.managerId ? parseInt(String(c.managerId), 10) : null,
      })),
      apiStatus: origin?.apiStatus ?? "Not Started",
      status: calculateStatus(
        origin?.apiStatus ?? "Not Started",
        v.startDate as moment.Moment | null | undefined,
        v.dueDate as moment.Moment | null | undefined,
        (v.checkpoints || []).map((c: any) => ({
          checkpointId: c?.checkpointId || 0,
          name: c?.name,
          dueDate: c?.dueDate ? (c.dueDate as moment.Moment).toISOString() : null,
          isCompleted: c?.isCompleted ?? false,
          completedAt: c?.completedAt,
        }))
      ) as Status,
      participants,
    };
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      const cur = currentValue();
      setSaving(true);
      const payload = {
        title: cur.title,
        description: cur.description,
        managerId: cur.managerId,
        assignees: (cur.assignees || [])
          .filter((x) => Number.isFinite(x.userId) && x.userId > 0)
          .map((x) => ({
            userId: x.userId,
            details: x.exec || "",
          })),
        assigneeIds: (cur.assignees || [])
          .map((x) => x.userId)
          .filter((id) => Number.isFinite(id) && id > 0),
        followerIds: (cur.followerIds || []).filter((id) => Number.isFinite(id) && id > 0),
        startDate: cur.startDate,
        dueDate: cur.dueDate,
        checkpoints: (cur.checkpoints || [])
          .filter((c) => c.name && c.name.toLowerCase() !== "final due date")
          .map((c: any) => ({
            checkpointId: c.checkpointId || c.CheckpointId || 0,
            name: c.name || c.Name || "",
            dueDate: c.dueDate || c.DueDate || null,
            managerId: c.managerId ? parseInt(String(c.managerId), 10) : null,
          })),
        status: cur.status,
      };
      if (process.env.NODE_ENV !== "production") {
        console.log("[TaskManagement] Submit payload", payload);
      }
      await apiUpdateTask(cur.id, payload);
      message.success("Saved");

      // 自动生成变更备注（Reschedule）
      if (origin) {
        const nameResolver = (id?: number | null) =>
          id != null
            ? userOpts.find((opt) => opt.value === id)?.label ||
              origin.participants?.find((p) => p.userId === id)?.name ||
              "-"
            : "-";
        const lines = buildDiffSummary(origin, cur, nameResolver);
        if (process.env.NODE_ENV !== "production") {
          console.log("[TaskManagement] Diff summary", lines);
        }
        if (lines.length) {
          const content =
            `Reschedule changes:\n` + lines.map((l) => `• ${l}`).join("\n");
          const notifyIds = Array.from(
            new Set(
              [
                cur.managerId && Number.isFinite(cur.managerId) ? cur.managerId : null,
                ...(cur.assignees || []).map((x) => (Number.isFinite(x.userId) ? x.userId : null)),
                ...(cur.followerIds || []).map((fid) => (Number.isFinite(fid) ? fid : null)),
              ].filter((val): val is number => !!val && val > 0)
            )
          );
          await apiAddComment(cur.id, {
            type: "Reschedule",
            content,
            notifyUserIds: notifyIds,
          });
          setCommentType("Reschedule");
          setCommentContent(content);
          setPeopleOptions(buildParticipantOptions());
          setNoticePeople([]);
          setNoticeVisible(true);
        }
      }
      
      // 跳转到任务列表页面
      history.push("/tasks");
    } catch (e: any) {
      if (!e?.errorFields) message.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openComment = (preset: CommentType) => {
    setCommentType(preset);
    setCommentContent("");
    setPeopleOptions(buildParticipantOptions());
    setNoticePeople([]);
    setCommentVisible(true);
  };

  const handleAddComment = async () => {
    if (!commentContent.trim()) return message.warning("Please input content");
    const taskNumericId = origin?.id ?? parseInt(id, 10);
    if (!taskNumericId) {
      return message.error("Task id is invalid.");
    }
    try {
      const notifyIds = Array.from(
        new Set((participants || []).map((p) => p.userId).filter((userId) => Number.isFinite(userId) && userId > 0))
      );
      await apiAddComment(taskNumericId, {
        type: commentType,
        content: commentContent.trim(),
        notifyUserIds: notifyIds,
      });
      setCommentVisible(false);
      setPeopleOptions(buildParticipantOptions());
      setNoticePeople([]);
      setNoticeVisible(true);
      message.success("Comment added");
    } catch (error: any) {
      console.error("Failed to add comment", error);
      const msg = error?.message;
      message.error(msg && msg !== "[object Object]" ? msg : "Failed to add comment");
    }
  };

  const handleCreateNotice = async () => {
    const taskNumericId = origin?.id ?? parseInt(id, 10);
    if (!taskNumericId) {
      return message.error("Task id is invalid.");
    }
    const title = form.getFieldValue("title") || origin?.title || "Task";
    const text = `${title} · ${commentType} comment is added.`;
    const baseIds = (participants || []).map((p) => p.userId).filter((userId) => Number.isFinite(userId) && userId > 0);
    const notifyIds =
      noticeVisibility === "All"
        ? baseIds
        : (noticePeople || [])
            .map((val) => parseInt(String(val), 10))
            .filter((userId) => Number.isFinite(userId) && userId > 0);
    if (!notifyIds.length) {
      return message.warning("Please select at least one recipient.");
    }
    try {
      await apiCreateNotice(taskNumericId, text, notifyIds);
      setNoticeVisible(false);
      setNoticePeople([]);
      message.success("Notice created");
    } catch (error: any) {
      console.error("Failed to create notice", error);
      const msg = error?.message;
      message.error(msg && msg !== "[object Object]" ? msg : "Failed to create notification");
    }
  };

  return (
    <div style={{ padding: 24, background: "#f7f9fc", minHeight: "100vh" }}>
      <Card
        loading={loading}
        bordered={false}
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          boxShadow: "0 2px 10px rgba(0,0,0,.06)",
        }}
        bodyStyle={{ padding: 24 }}
      >
        {/* Header */}
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={2}>
              <Title level={3} style={{ margin: 0 }}>
                Task Management
              </Title>
              <Text type="secondary">
                Edit task fields. Saving with changes will auto-create a
                Reschedule comment.
              </Text>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={load}>
                Refresh
              </Button>
              <Button icon={<RollbackOutlined />} onClick={() => history.goBack()}>
                Back
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={handleSave}
              >
                Save
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider style={{ margin: "16px 0" }} />

        {/* Form */}
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: "Please input title" }]}
              >
                <Input placeholder="Task Title" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="description" label="Task Description">
                <TextArea rows={4} placeholder="Describe the task…" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="managerId"
                label="Task Manager"
                rules={[{ required: true, message: "Select manager" }]}
              >
                <Select
                  showSearch
                  placeholder="Select manager"
                  onSearch={onSearchUsers}
                  onFocus={onUserFocus}
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                >
                  {userOpts.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: "Select status" }]}
                initialValue={STATUS_OPTIONS[0]}
              >
                <Select 
                  placeholder="Status (auto-calculated)" 
                  disabled
                  style={{ cursor: "not-allowed" }}
                >
                  <Option value={computedStatus}>
                    <span
                      style={{
                        color: STATUS_COLOR_MAP[computedStatus],
                        fontWeight: 600,
                      }}
                    >
                      {computedStatus}
                    </span>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="startDate"
                label="Start Date"
                rules={[{ required: true, message: "Pick start date" }]}
              >
                <DatePicker style={{ width: "100%" }} format="YYYY/MM/DD" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="dueDate"
                label="Due Date"
                rules={[{ required: true, message: "Pick due date" }]}
              >
                <DatePicker style={{ width: "100%" }} format="YYYY/MM/DD" />
              </Form.Item>
            </Col>
          </Row>

          {/* Assignees with execution details */}
          <Form.List name="assignees">
            {(fields, { add, remove }) => (
              <>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text strong>Assignee</Text>
                  </Col>
                  <Col>
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => add({ userId: undefined, exec: "" })}
                    >
                      Add Assignee
                    </Button>
                  </Col>
                </Row>
                <Divider style={{ margin: "8px 0 16px" }} />
                {fields.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No assignees"
                  />
                ) : null}
                {fields.map((f) => (
                  <Row key={f.key} gutter={12} align="top" style={{ marginBottom: 8 }}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name={[f.name, "userId"]}
                        rules={[{ required: true, message: "Select assignee" }]}
                        label={f.name === 0 ? "Name" : undefined}
                      >
                        <Select
                          showSearch
                          placeholder="Select assignee"
                          onSearch={onSearchUsers}
                          onFocus={onUserFocus}
                          filterOption={(input, option) =>
                            String(option?.children ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          allowClear
                        >
                          {userOpts.map((o) => (
                            <Option key={o.value} value={o.value}>
                              {o.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={14}>
                      <Form.Item
                        name={[f.name, "exec"]}
                        label={f.name === 0 ? "Execution Details" : undefined}
                      >
                        <Input placeholder="Optional execution details…" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={2}>
                      <Button
                        style={{ 
                          marginTop: f.name === 0 ? 29 : 0,
                          background: "transparent",
                          border: "none",
                          padding: 0,
                        }}
                        icon={<CloseOutlined style={{ color: "#000", fontSize: "16px" }} />}
                        onClick={() => remove(f.name)}
                      />
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Form.List>

          {/* Followers */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Form.Item name="followerIds" label="Followers">
                <Select
                  mode="multiple"
                  showSearch
                  onSearch={onSearchUsers}
                  onFocus={onUserFocus}
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  placeholder="Select followers"
                >
                  {userOpts.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Checkpoints */}
          <Form.List name="checkpoints">
            {(fields, { add, remove }) => (
              <>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text strong>Checkpoint</Text>
                  </Col>
                  <Col>
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => add({ name: "", checkpointId: 0 })}
                    >
                      Add Checkpoint
                    </Button>
                  </Col>
                </Row>
                <Divider style={{ margin: "8px 0 16px" }} />
                {fields.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No checkpoints"
                  />
                ) : null}
                {fields.map((f) => {
                  const checkpointName = form.getFieldValue(["checkpoints", f.name, "name"]);
                  const isFinalDueDate = checkpointName && 
                    checkpointName.toLowerCase() === "final due date";
                  
                  return (
                    <Row key={f.key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                      <Form.Item name={[f.name, "checkpointId"]} hidden>
                        <Input type="hidden" />
                      </Form.Item>
                      <Col xs={24} md={7}>
                        <Form.Item
                          name={[f.name, "name"]}
                          rules={[{ required: true, message: "Input checkpoint name" }]}
                          label={f.name === 0 ? "Name" : undefined}
                        >
                          <Input 
                            placeholder="Checkpoint name" 
                            disabled={isFinalDueDate}
                            style={isFinalDueDate ? { backgroundColor: "#f5f5f5" } : {}}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item
                          name={[f.name, "dueDate"]}
                          label={f.name === 0 ? "Due Date" : undefined}
                        >
                          <DatePicker 
                            style={{ width: "100%" }} 
                            format="YYYY/MM/DD"
                            disabled={isFinalDueDate}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={9}>
                        <Form.Item
                          name={[f.name, "managerId"]}
                          label={f.name === 0 ? "Checkpoint Owner" : undefined}
                        >
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select checkpoint owner"
                            onSearch={onSearchUsers}
                            onFocus={onUserFocus}
                            filterOption={(input, option) =>
                              String(option?.children ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            disabled={isFinalDueDate}
                          >
                            {userOpts.map((o) => (
                              <Option key={o.value} value={o.value}>
                                {o.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={2}>
                        {!isFinalDueDate && (
                          <Button
                            style={{ 
                              marginTop: f.name === 0 ? 29 : 0,
                              background: "transparent",
                              border: "none",
                              padding: 0,
                            }}
                            icon={<CloseOutlined style={{ color: "#000", fontSize: "16px" }} />}
                            onClick={() => remove(f.name)}
                          />
                        )}
                      </Col>
                    </Row>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>
      </Card>

      {/* Add Comment (AntD v4：visible) */}
      <Modal
        title="Add Comment"
        visible={commentVisible}
        okText="Add"
        onOk={handleAddComment}
        onCancel={() => setCommentVisible(false)}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Select value={commentType} onChange={(v: CommentType) => setCommentType(v)}>
            <Option value="Routine">Routine</Option>
            <Option value="Apply Reschedule">Apply Reschedule</Option>
            <Option value="Apply Completion">Apply Completion</Option>
          </Select>
          <TextArea
            rows={5}
            placeholder="Write your comment…"
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
          />
        </Space>
      </Modal>

      {/* Notification */}
      <Modal
        title="Notification"
        visible={noticeVisible}
        okText="Confirm"
        onOk={handleCreateNotice}
        onCancel={() => setNoticeVisible(false)}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>
            Creator: <Text strong>current account</Text> (fixed)
          </Text>
          <Select
            value={noticeVisibility}
            onChange={(v: "All" | "Selected") => setNoticeVisibility(v)}
          >
            <Option value="Selected">To Selected people</Option>
            <Option value="All">To All in this task</Option>
          </Select>
          {noticeVisibility === "Selected" && (
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select people"
              value={noticePeople}
              onChange={(vals) => setNoticePeople(vals as string[])}
              filterOption={(input, option) =>
                String(option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {peopleOptions.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          )}
          <TextArea
            rows={3}
            readOnly
            value={`${form.getFieldValue("title") || origin?.title || "Task"} · ${commentType} comment is added.`}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default TaskManagementPage;
